import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class DeploymentsService {
    /**
     * Update Supervision
     * Handle a user's update for the supervision state of a given action.
     *
     * This will, after validating that the user has permissions for this
     * deployment, send down a message over the deployment supervision websocket.
     * @param deploymentId
     * @param requestBody
     * @returns any Successful Response
     * @throws ApiError
     */
    static updateSupervision(deploymentId, requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/deploy/supervise/{deployment_id}/action',
            path: {
                'deployment_id': deploymentId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Subscribe To App
     * SSE provider endpoint for deployed app changes.
     * @param jobId
     * @param includeDeleted
     * @returns AppInstanceUpdate Successful Response
     * @throws ApiError
     */
    static subscribeToApp(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/deploy/{job_id}/subscribe',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Deployed Apps
     * @param jobId
     * @param includeDeleted
     * @returns DeployedApps Successful Response
     * @throws ApiError
     */
    static getDeployedApps(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/deploy/{job_id}',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Deploy Job
     * Deploy the job from the job id in the url params.
     *
     * Should only deploy if the job exists and the job result is code.
     * @param jobId
     * @param includeDeleted
     * @param requestBody
     * @returns DeploymentCreationResponse Successful Response
     * @throws ApiError
     */
    static deployJob(jobId, includeDeleted = false, requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/deploy/{job_id}',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Remove App
     * Stops the most recent app that was started from this job, if any are
     * running.
     *
     * This will also remove any previous instances that are still running,
     * including cleaning up their docker container.
     * @param jobId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static removeApp(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/deploy/{job_id}',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Create Deployment Token
     * Development endpoint to create a dev token for the user.
     * @param requestBody
     * @returns string Successful Response
     * @throws ApiError
     */
    static createDeploymentToken(requestBody) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/deploy/token',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Allow Deployment Support
     * Development endpoint to move a deployment from the `SUPPORT_REQUESTED`
     * state to the `READY` state, so that it can be deployed again.
     * @param jobId
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static allowDeploymentSupport(jobId, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/deploy/{job_id}/support',
            path: {
                'job_id': jobId,
            },
            query: {
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get App History
     * Get a time-bounded history for the specified app and job.
     *
     * This is using the database directly due to pydantic deserialization errors
     * with the generic AppHistoryEvent inner types. This isn't _good_, but it
     * does allow us to break out the events from the app itself.
     * @param appId
     * @param jobId
     * @param start
     * @param end
     * @param includeDeleted
     * @returns any Successful Response
     * @throws ApiError
     */
    static getAppHistory(appId, jobId, start, end, includeDeleted = false) {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/deploy/{job_id}/{app_id}/history',
            path: {
                'app_id': appId,
                'job_id': jobId,
            },
            query: {
                'start': start,
                'end': end,
                'include_deleted': includeDeleted,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
}
